/* ============================================================
   HONEY BAE | lander H, "the affair question" : the engine

   Ported from Veyla's lander H, which is why the slot contract, the beat
   renderer and the offer panel are its. What is different is the brand, the
   offer module (one variant on a selling plan rather than a bundle ladder) and
   one behaviour: five of the ten beats have no photograph, so the renderer
   marks those rows `is-solo` and the stylesheet collapses them to the reading
   column instead of leaving a column of white.

   Four jobs:

   1. PAINT THE SOURCED LINES. Every brand claim, every outside figure and every
      word of the offer panel comes out of hn-source.js at runtime into a
      [data-s] slot, so the claims are in one auditable file rather than
      scattered through the markup.

   2. BUILD THE FURNITURE. The band, the ticker, the chip rail, the stat band,
      the image cards, the "how" steps, the reviews and the appended FAQ entries
      are all claim-bearing, so they are built here FROM THE REGISTER. The markup
      carries empty hosts and nothing else.

   3. PLACE THE PHOTOGRAPHS. Every image comes from the ART register with the
      pixel size of its file beside it, which is what lets the harness check a
      declared size against the file on disk.

   4. ROUTE. This page does not sell. Every door on it opens the SAME product
      page, and the panel says out loud that the plan is chosen there.

   WHAT ROUTING CHANGES ABOUT THE NUMBERS. A page with a cart in it may quote
   the whole ladder, because the quote and the charge are the same surface. This
   one is not, so it quotes ONE thing, the plan the supplied script names, and
   every figure it shows derives from a single number in hb-offer.js. A
   repricing moves the page, not a developer.
   ============================================================ */
(() => {
  const O = window.HB_OFFER;
  const S = window.HN_SOURCE;
  if (!O || !S) {
    console.error('[hn] HB_OFFER or HN_SOURCE missing. Serve from the BRAND root:\n' +
      '  cd succession/brands/honey-bae && python3 -m http.server 8797 --bind 127.0.0.1\n' +
      '  then open /listicles/notice/');
    return;
  }

  const $  = s => document.querySelector(s);
  const $$ = s => Array.from(document.querySelectorAll(s));
  const esc = s => String(s).replace(/[&<>"]/g, m =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[m]));

  /* Images the ENGINE applies rather than the markup. On the theme assets/ is
     flat and these relative paths do not exist, so the layout emits a map from
     local filename to CDN url and this resolves through it. */
  const imgUrl = p =>
    (window.HN_IMG && window.HN_IMG[p.split('/').pop()]) || p;

  /* Every declared shot, flat, so anything can name one by filename and get the
     register's own width and height with it. */
  const ALL_ART = [S.ART.hero, S.ART.cutout, S.ART.close]
    .concat(S.ART.beats).concat(S.ART.library || []).filter(a => a && a.src);
  const artByFile = f => ALL_ART.find(a => a.src.split('/').pop() === f);

  /* ---------- THE ONE NUMBER, and everything off it ----------
     The plan the supplied script names. Nothing is selected by a reader on this
     page, so this is a constant rather than a state machine. */
  const PLAN = O.PLAN_DEFAULT;

  const dealCents  = () => O.planCents(PLAN);
  const listCents  = () => O.LIST_CENTS;
  const savePct    = () => O.planSavePct(PLAN);
  const saveCents  = () => O.planSaveCents(PLAN);
  const perSachet  = () => O.perServingCents(PLAN);

  const SLOTS = {
    /* the supplied script */
    'head-top':   S.SCRIPT.headTop,
    'head-mid':   S.SCRIPT.headMid,
    'head-em':    S.SCRIPT.headEm,
    'intro':      S.SCRIPT.intro,

    /* THE BYLINE AND THE LINE THAT HAS TO TRAVEL WITH IT. The article is a
       composite written in the first person and the supplied script's own
       publishing note requires the disclosure, so it is a slot in the register
       rather than a sentence somebody can delete from the markup without
       noticing what it was doing. */
    'byline':      S.SCRIPT.byline,
    'byline-note': S.SCRIPT.bylineNote,

    'close-head': S.SCRIPT.closeHead,
    'close':      S.SCRIPT.close,
    'recommend':  S.SCRIPT.recommend,
    'signoff':    S.SCRIPT.signoff,

    /* Honey Bae's own, always inside a labelled note */
    'brand-what':            S.BRAND.what,
    'brand-botanicals':      S.BRAND.botanicals,
    'brand-botanicals-note': S.BRAND.botanicalsNote,
    'brand-guarantee':       S.BRAND.guarantee,
    'brand-medical':         S.BRAND.medical,

    'out-packet-label': S.OUTSIDE.packet.label,
    'out-packet-cost':  S.OUTSIDE.packet.cost,
    'out-packet-note':  S.OUTSIDE.packet.note,
    'out-doctor-label': S.OUTSIDE.doctor.label,
    'out-doctor-cost':  S.OUTSIDE.doctor.cost,
    'out-doctor-note':  S.OUTSIDE.doctor.note
  };

  function paint() {
    $$('[data-s]').forEach(el => {
      const val = SLOTS[el.getAttribute('data-s')];
      if (val) el.textContent = val;
    });
    $$('[data-hn-price]').forEach(n => { n.textContent = O.money(dealCents()); });
    $$('[data-hn-list]').forEach(n  => { n.textContent = O.money(listCents()); });
    $$('[data-hn-save-pct]').forEach(n => { n.textContent = savePct() + '%'; });

    art();
    renderBeats();
    renderFurniture();
  }

  /* ---------- the photographs ----------
     One helper, used by every slot, so the width/height pair always comes from
     the register rather than from whoever wrote the markup. loading="eager" on
     the hero because lazy-loading the thing above the fold delays it for no
     gain. */
  const figure = (a, eager) =>
    `<img src="${imgUrl(a.src)}" alt="${esc(a.alt)}" width="${a.w}" height="${a.h}" ` +
    `class="${a.w === a.h ? 'is-sq' : ''}" ` +
    `loading="${eager ? 'eager' : 'lazy'}" decoding="async">`;

  /* A DECLARED SLOT THAT HAS NO FILE YET RENDERS AS A MARKED FRAME, not as
     nothing and not as a borrowed photograph. It holds its own aspect ratio, so
     the section is laid out at its final height from the first build, and it
     says on its face what it is waiting for, so it cannot be pushed live by
     accident and mistaken for a design. build-listicle-theme.py warns on every
     placeholder still in the register. */
  const slot = a => {
    if (!a) return '';
    if (!a.placeholder) return figure(a);
    return `<div class="hn-slot" style="aspect-ratio:${a.ratio || '3 / 2'}">
        <span class="hn-slot-tag">${esc(a.caption || 'Image slot')}</span>
        <b>${esc(a.wants)}</b>
        <span>${a.w} x ${a.h}</span>
        <em>${esc(a.note || '')}</em>
      </div>`;
  };

  function art() {
    const hero = $('[data-hn-art="hero"]');
    if (hero) hero.innerHTML = figure(S.ART.hero, true);

    const close = $('[data-hn-art="close"]');
    if (close) close.innerHTML = slot(S.ART.close);
  }

  /* The story, built from the script. The art alternates sides down the page,
     which the stylesheet does off :nth-of-type, so the order here is the
     script's order and nothing else.

     THE BEATS ARE LABELLED, NOT NUMBERED. Each label comes from the register
     with its copy.

     ART IS PAIRED BY INDEX AND MAY BE null. Five of the ten beats have no
     photograph on purpose: the library is product photography, and there is no
     honest picture of a man lying awake at 2 AM that is not a stock model
     standing in for a narrator who does not exist. Those rows get `is-solo` so
     the copy centres in the reading column rather than sitting beside a gap. */
  function renderBeats() {
    const host = $('[data-hn-beats]');
    if (!host) return;
    host.innerHTML = S.SCRIPT.beats.map((b, i) => {
      const a = S.ART.beats[i];
      return `
      <div class="hn-beat${a ? '' : ' is-solo'}">
        ${a ? `<figure class="hn-beat-shot">${figure(a)}</figure>` : ''}
        <div class="hn-beat-copy">
          <span class="hn-pill"><s></s>${esc(b.pill)}</span>
          <h2>${esc(b.head)}</h2>
          <p>${esc(b.body)}</p>
        </div>
      </div>`;
    }).join('');
  }

  /* ---------- the furniture ----------
     Band, ticker, chips, stat band, image cards, the "how" steps, reviews and
     the appended FAQ entries. All claim-bearing, so all built from the
     register. */
  function renderFurniture() {
    const band = $('[data-hn-band]');
    if (band) band.innerHTML =
      `<b>${esc(S.BRAND.band.head)}</b> <span>${esc(S.BRAND.band.note)}</span>`;

    const tick = $('[data-hn-ticker]');
    if (tick) {
      const run = S.BRAND.ticker.map(t => esc(t)).join(' <i>&bull;</i> ');
      /* twice, because the keyframe translates the track by half its width to
         loop seamlessly. One copy would slide off and leave a gap. */
      tick.innerHTML = `<span>${run} <i>&bull;</i> </span><span>${run} <i>&bull;</i> </span>`;
    }

    const chips = $('[data-hn-chips]');
    if (chips) chips.innerHTML = S.BRAND.chips
      .map(c => `<span class="${c.hi ? 'is-hi' : ''}">${esc(c.t)}</span>`).join('');

    const quad = $('[data-hn-quad]');
    if (quad) quad.innerHTML = S.BRAND.stats
      .map(s => `<div><b>${esc(s.n)}</b><span>${esc(s.l)}</span></div>`).join('');

    /* THE PICTURE IS A THUMBNAIL RATHER THAN A BACKGROUND. See BRAND.cards in
       the register for why: Honey Bae's usable library is cutouts and burned-in
       infographics, and neither survives having a heading printed over it. */
    const cards = $('[data-hn-cards]');
    if (cards) cards.innerHTML = S.BRAND.cards.map(c => {
      const a = artByFile(c.img.split('/').pop());
      return `
      <div class="hn-ic">
        <img class="hn-ic-thumb" src="${imgUrl(c.img)}" alt=""
             ${a ? `width="${a.w}" height="${a.h}"` : ''} loading="lazy" decoding="async">
        <div class="hn-ic-tags"><i>${esc(c.tag)}</i></div>
        <h3>${esc(c.head)}</h3>
        <p>${esc(c.body)}</p>
      </div>`;
    }).join('');

    const how = $('[data-hn-how]');
    if (how) how.innerHTML = S.BRAND.how.map(s => `
      <div class="hn-step">
        <span class="hn-step-ico">${esc(s.ico)}</span>
        <span class="hn-step-wk">${esc(s.wk)}</span>
        <h4>${esc(s.head)}</h4>
        <p>${esc(s.body)}</p>
      </div>`).join('');

    renderReviews();

    /* The disclosures that live in the register are appended to the FAQ rather
       than typed into it, so a component can go and a disclosure cannot. */
    const faq = $('[data-hn-faq]');
    if (faq) faq.insertAdjacentHTML('beforeend', S.BRAND.faqExtra.map(a => `
      <details>
        <summary>${esc(a.q)}</summary>
        <p>${esc(a.a)}</p>
      </details>`).join(''));
  }

  /* ---------- the reviews ----------
     Real store reviews, named, painted from the register. Two slots: the pull
     quote in the article and the grid under the FAQ.

     The star row is drawn from the review's own rating rather than fixed at
     five, so a four star review can never be drawn as five. */
  const stars = n => '&#9733;'.repeat(n) + '<span style="opacity:.34">' +
    '&#9733;'.repeat(Math.max(0, 5 - n)) + '</span>';

  function renderReviews() {
    const R = S.REVIEWS;
    if (!R) return;

    const pull = $('[data-hn-pull]');
    if (pull && R.pull) pull.innerHTML =
      `<p>&ldquo;${esc(R.pull.body)}&rdquo;</p>` +
      `<cite>${esc(R.pull.name)}<span>${esc(R.pull.tag)}</span></cite>`;

    const revs = $('[data-hn-revs]');
    if (revs) revs.innerHTML = (R.list || []).map(r => `
      <div class="hn-rev">
        <div class="hn-rev-top">
          <span class="hn-rev-name">${esc(r.name)}</span>
          <span class="hn-rev-stars">${stars(r.stars)}</span>
        </div>
        <p>${esc(r.body)}</p>
      </div>`).join('');
  }

  /* ============================================================
     THE OFFER PANEL

     THIS PAGE ROUTES. It quotes the offer the script's close promises, and it
     says where the real one is, because a quote is only safe on a page that
     does not charge if it names the surface that does. Every figure in it comes
     off ONE number in hb-offer.js.
     ============================================================ */

  const PDP = S.BUY.pdpUrl || O.PRODUCT.url;

  /* Every door out of this page, in one place, so a link cannot be left
     pointing at something that is not there any more. */
  function renderRoute() {
    $$('[data-hn-pdp]').forEach(a => {
      a.setAttribute('href', PDP);
      if (!a.textContent.trim()) a.textContent = S.BUY.cta;
    });

    const host = $('[data-hn-route]');
    if (!host) return;

    const price = O.money(dealCents());
    const list  = O.money(listCents());
    const saved = O.money(saveCents());

    host.innerHTML = `
      <div class="hn-route-shot">${figure(S.ART.cutout)}</div>

      <div class="hn-route-body">
        <span class="hn-route-badge">${esc(S.BUY.badge)}</span>
        <span class="hn-route-eyebrow">${esc(S.BUY.eyebrow)}</span>

        <!-- The only percentage on the page, and it is arithmetic: the plan
             discount the store itself applies. -->
        <h3 class="hn-route-head">
          <b>Save ${savePct()}%</b> <em>${esc(S.BUY.headTail)}</em>
        </h3>
        <p class="hn-route-line">${price} a pack <s>instead of ${list}</s></p>
        <p class="hn-route-sub">That is ${saved} off every pack, plus free priority shipping
           and 5 free Gold Shilajit gummies with the welcome kit.</p>

        <a class="hn-btn hn-route-cta" data-hn-pdp href="${PDP}">${esc(S.BUY.cta)}</a>

        <ul class="hn-route-stats">
          ${S.BUY.stats.map(x => `<li><b>${esc(x.n)}</b><span>${esc(x.l)}</span></li>`).join('')}
        </ul>

        <p class="hn-route-note">${esc(S.BUY.note)}</p>
      </div>`;
  }

  function wire() {
    const sticky = $('[data-hn-sticky]');
    const hero = $('.hn-hero');
    if (sticky && hero && 'IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => {
        sticky.classList.toggle('is-in', !e.isIntersecting);
      }, { rootMargin: '-120px 0px 0px 0px' }).observe(hero);
    }
  }

  function render() {
    renderRoute();

    /* The bar follows the reader down the page, so it carries no price and no
       percentage: a figure that travels with her is one she cannot check
       without leaving, and this page is not the one that charges. It names the
       product and its button opens the product page. */
    const st = $('[data-hn-sticky-tier]');
    const ss = $('[data-hn-sticky-sub]');
    if (st) st.textContent = S.BUY.stickyHead;
    if (ss) ss.textContent = S.BUY.stickySub;
  }

  paint();
  wire();
  render();

  window.HN = {
    PLAN, PDP, dealCents, listCents, savePct, saveCents, perSachet,
    renderRoute, artByFile
  };
})();
